import { useState } from 'react'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { useAsync } from '../lib/useAsync'
import { MACHINE_ROLES, canIssueTokens, canWrite, type Role, type Token, type User } from '../lib/types'
import { TokenReveal } from '../components/TokenReveal'
import { Button, Callout, EmptyState, Spinner, cx } from '../components/ui'

const ROLES: Role[] = ['user', 'employee', 'developer', 'admin', 'root']
type Tab = 'users' | 'machines' | 'tokens' | 'structure'

export function AdminPage() {
  const { admin, role, loading } = useAuth()
  const writer = canWrite(role)
  const [tab, setTab] = useState<Tab>(admin ? 'users' : 'structure')

  if (loading) return <Wrap><Spinner /></Wrap>
  if (!writer)
    return (
      <Wrap>
        <EmptyState title="Staff only">
          Authoring requires the employee, developer, admin or root role.
        </EmptyState>
      </Wrap>
    )

  // Anyone who may author sees Structure; token self-service reaches down to
  // employee, while identity management stays with admins.
  const tabs: Tab[] = [
    ...(admin ? (['users', 'machines'] as Tab[]) : []),
    ...(canIssueTokens(role) ? (['tokens'] as Tab[]) : []),
    'structure',
  ]

  return (
    <Wrap>
      <h1 className="text-2xl font-semibold tracking-tight text-ink">
        {admin ? 'Admin' : 'Authoring'}
      </h1>

      <div className="mt-6 flex gap-1 border-b border-line">
        {tabs.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cx(
              '-mb-px border-b-2 px-3 py-2 text-sm font-medium capitalize transition-colors',
              tab === t
                ? 'border-accent text-ink'
                : 'border-transparent text-muted hover:text-ink',
            )}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === 'users' && <Users />}
        {tab === 'machines' && <Machines />}
        {tab === 'tokens' && <Tokens />}
        {tab === 'structure' && <Structure />}
      </div>
    </Wrap>
  )
}

/* ------------------------------------------------------------------- Users */

function Users() {
  const { email: myEmail, refresh } = useAuth()
  const { data, loading, error } = useAsync((signal) => api.admin.users(signal), [])
  const [edits, setEdits] = useState<Record<number, Partial<User>>>({})
  const [saving, setSaving] = useState<number | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  if (loading) return <Spinner />
  if (error) return <p className="text-sm text-danger">Could not load users.</p>
  if (!data?.length) return <EmptyState title="No users yet" />

  const save = async (u: User, patch: Partial<User>) => {
    setSaving(u.id)
    try {
      await api.admin.updateUser(u.id, patch)
      setEdits((e) => ({ ...e, [u.id]: { ...e[u.id], ...patch } }))

      if (patch.role === undefined) return
      // The backend rewrites the `role` claim on the target's next authenticated
      // request, but their current token still carries the old one — and Storage
      // rules read the claim, not Postgres. So the change lands only on refresh.
      if (u.email === myEmail) {
        await refresh()
        setNotice('Your own role changed; your token has been refreshed.')
      } else {
        setNotice(`${u.email} is now “${patch.role}”. They must sign out and back in for it to take effect.`)
      }
    } finally {
      setSaving(null)
    }
  }

  return (
    <div className="space-y-4">
      {notice && <Callout tone="warn">{notice}</Callout>}
      <div className="overflow-x-auto rounded-[var(--radius-sh)] border border-line">
      <table className="w-full text-sm">
        <thead className="bg-sunken text-left text-xs text-subtle">
          <tr>
            <th className="px-4 py-2.5 font-semibold">Email</th>
            <th className="px-4 py-2.5 font-semibold">Role</th>
            <th className="px-4 py-2.5 font-semibold">Active</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {data.map((u) => {
            const role = edits[u.id]?.role ?? u.role
            const active = edits[u.id]?.active ?? u.active
            // Deactivating or demoting yourself locks you out of this very page,
            // and only a SQL bootstrap can undo it. Never offer the foot-gun.
            const isSelf = u.email === myEmail
            return (
              <tr key={u.id} className={cx(saving === u.id && 'opacity-50')}>
                <td className="px-4 py-2.5 text-ink">
                  <span className="flex items-center gap-2">
                    {u.email}
                    {isSelf && <span className="text-xs text-subtle">(you)</span>}
                    {u.machine && (
                      <span className="rounded-full bg-accent-soft px-2 py-0.5 text-[10px] font-semibold tracking-wide text-accent-text uppercase">
                        Machine
                      </span>
                    )}
                  </span>
                </td>
                <td className="px-4 py-2.5">
                  <select
                    value={role}
                    title={isSelf ? 'You cannot change your own role.' : undefined}
                    disabled={saving === u.id || isSelf}
                    onChange={(e) => void save(u, { role: e.target.value as Role })}
                    className="rounded-[var(--radius-sh)] border border-line bg-surface px-2 py-1 font-mono text-xs text-ink"
                  >
                    {/* A machine must never be promoted past developer. */}
                    {(u.machine ? MACHINE_ROLES : ROLES).map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-2.5">
                  <input
                    type="checkbox"
                    checked={active}
                    title={isSelf ? 'You cannot deactivate yourself.' : undefined}
                    disabled={saving === u.id || isSelf}
                    onChange={(e) => void save(u, { active: e.target.checked })}
                    className="size-4 accent-[var(--color-accent)] disabled:opacity-50"
                  />
                </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ Tokens */

function Tokens() {
  const { admin } = useAuth()
  // Non-admins only ever see their own; `?all=1` is refused for them anyway.
  const { data, loading, error } = useAsync(() => api.admin.tokens(admin), [admin])
  const [list, setList] = useState<Token[] | null>(null)
  const [fresh, setFresh] = useState<Token | null>(null)
  const [name, setName] = useState('')
  const [forEmail, setForEmail] = useState('')
  // The backend defaults omitted scopes to ["write"], so an unchecked box would lie.
  const [write, setWrite] = useState(true)
  const [expiresAt, setExpiresAt] = useState('')
  const [failed, setFailed] = useState<string | null>(null)

  const tokens = list ?? data ?? []

  const create = async () => {
    setFailed(null)
    try {
      const t = await api.admin.createToken({
        name: name || undefined,
        forEmail: admin && forEmail ? forEmail : undefined,
        scopes: write ? ['write'] : [],
        expiresAt: expiresAt ? new Date(`${expiresAt}T00:00:00Z`).toISOString() : undefined,
      })
      setFresh(t)
      // Keep the secret out of the list; only the modal ever holds it.
      const { raw_token: _secret, ...listed } = t
      setList([...tokens, listed])
      setName('')
      setForEmail('')
    } catch (e) {
      // `not_machine` when for_email names a person; `cannot_write` when the owner's
      // role does not permit the write scope. Both are worth showing verbatim.
      setFailed(e instanceof Error ? e.message : 'Could not issue the token.')
    }
  }

  const revoke = async (id: number) => {
    await api.admin.revokeToken(id)
    setList(tokens.map((t) => (t.id === id ? { ...t, revoked: true } : t)))
  }

  if (loading) return <Spinner />
  if (error) return <p className="text-sm text-danger">Could not load tokens.</p>

  return (
    <div className="space-y-6">
      {/* The secret leaves state the moment the modal closes; it is never logged. */}
      {fresh?.raw_token && <TokenReveal token={fresh} onClose={() => setFresh(null)} />}

      <div className="rounded-[var(--radius-sh)] border border-line p-4">
        <h2 className="text-sm font-semibold text-ink">Issue a token</h2>
        <p className="mt-1 text-xs text-muted">
          {admin
            ? 'Leave the machine field empty to mint one for yourself. Naming a machine issues the token on its behalf and records you as the requester — a token can never be issued for another person.'
            : 'Mints a token that acts as you. Use it from your IDE to publish documentation.'}
        </p>

        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Token name, e.g. “vaqif macbook”"
            className="h-9 rounded-[var(--radius-sh)] border border-line bg-surface px-3 text-sm text-ink placeholder:text-subtle focus:border-accent focus:outline-none"
          />
          {/* `for_email` is admin-only; a developer sending it gets 403 forbidden. */}
          {admin && (
            <input
              value={forEmail}
              onChange={(e) => setForEmail(e.target.value)}
              placeholder="Machine email (optional)"
              className="h-9 rounded-[var(--radius-sh)] border border-line bg-surface px-3 font-mono text-sm text-ink placeholder:text-subtle focus:border-accent focus:outline-none"
            />
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-muted">
            <input
              type="checkbox"
              checked={write}
              onChange={(e) => setWrite(e.target.checked)}
              className="size-4 accent-[var(--color-accent)]"
            />
            <code className="font-mono text-xs">write</code> scope — required to publish docs
          </label>

          <label className="flex items-center gap-2 text-sm text-muted">
            Expires
            <input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="h-9 rounded-[var(--radius-sh)] border border-line bg-surface px-2 text-sm text-ink focus:border-accent focus:outline-none"
            />
          </label>

          <Button variant="primary" onClick={() => void create()}>
            Issue token
          </Button>
        </div>

        {failed && <p className="mt-2 text-xs text-danger">{failed}</p>}
      </div>

      {tokens.length === 0 ? (
        <EmptyState title="No access tokens" />
      ) : (
        <ul className="divide-y divide-line rounded-[var(--radius-sh)] border border-line">
          {tokens.map((t) => (
            <li
              key={t.id}
              className={cx('flex items-center justify-between gap-4 px-4 py-3', t.revoked && 'opacity-50')}
            >
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-2 text-sm text-ink">
                  <span className="truncate">{t.name ?? `Token #${t.id}`}</span>
                  <code className="font-mono text-xs text-subtle">{t.prefix}</code>
                  {t.owner_is_machine && <Pill>Machine</Pill>}
                  {t.scopes?.map((s) => <Pill key={s}>{s}</Pill>)}
                  {t.revoked && <Pill tone="danger">Revoked</Pill>}
                  {!t.revoked && isExpired(t.expires_at) && <Pill tone="danger">Expired</Pill>}
                </p>
                <p className="mt-0.5 truncate font-mono text-xs text-subtle">
                  {t.owner ?? '—'}
                  {t.created_by && t.created_by !== t.owner && ` · issued by ${t.created_by}`}
                  {t.expires_at && ` · expires ${new Date(t.expires_at).toLocaleDateString()}`}
                  {` · last used ${t.last_used_at ? new Date(t.last_used_at).toLocaleDateString() : 'never'}`}
                </p>
              </div>
              {!t.revoked && (
                <Button size="sm" variant="ghost" onClick={() => void revoke(t.id)}>
                  Revoke
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

const isExpired = (iso?: string) => !!iso && new Date(iso).getTime() < Date.now()

function Pill({ children, tone }: { children: React.ReactNode; tone?: 'danger' }) {
  return (
    <span
      className={cx(
        'rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide uppercase',
        tone === 'danger' ? 'bg-danger/15 text-danger' : 'bg-accent-soft text-accent-text',
      )}
    >
      {children}
    </span>
  )
}

/* ---------------------------------------------------------------- Machines */

function Machines() {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<Role>('developer')
  const [result, setResult] = useState<string | null>(null)
  const [failed, setFailed] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setResult(null)
    setFailed(null)
    try {
      // `machine: true` is implied — this endpoint only ever creates machines.
      const u = await api.admin.createMachine({ email, role })
      setResult(`Machine “${u.email}” registered as ${u.role}. Issue it a token under Tokens.`)
      setEmail('')
    } catch (err) {
      // 409 exists · 400 bad_role · 400 bad_input
      setFailed(err instanceof Error ? err.message : 'Could not register the machine.')
    }
  }

  return (
    <div className="max-w-lg space-y-4">
      <div>
        <h2 className="font-semibold text-ink">Register a machine identity</h2>
        <p className="mt-1 text-sm text-muted">
          A principal that never signs in with Google and exists only to carry a token — CI, a
          publishing bot, an IDE agent. Revisions are attributed to it, and revoking its token
          leaves human access untouched. It can never be an admin.
        </p>
      </div>

      {result && <Callout>{result}</Callout>}
      {failed && <Callout tone="danger">{failed}</Callout>}

      <form className="space-y-3" onSubmit={submit}>
        <Field label="Machine email" value={email} onChange={setEmail} required />
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-muted">Role</span>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            className="h-9 rounded-[var(--radius-sh)] border border-line bg-surface px-2 font-mono text-sm text-ink"
          >
            {MACHINE_ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
        <Button type="submit" variant="primary" size="sm">
          Register machine
        </Button>
      </form>
    </div>
  )
}

/* --------------------------------------------------------------- Structure */

function Structure() {
  const { admin } = useAuth()
  const [msg, setMsg] = useState<string | null>(null)
  const [svc, setSvc] = useState({ slug: '', title: '', description: '' })
  const [topic, setTopic] = useState({ service: '', path: '', title: '' })

  const run = async (fn: () => Promise<unknown>, ok: string) => {
    try {
      await fn()
      setMsg(ok)
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Failed.')
    }
  }

  return (
    <div className="grid gap-8 sm:grid-cols-2">
      {msg && (
        <div className="sm:col-span-2">
          <Callout>{msg}</Callout>
        </div>
      )}

      <form
        className={cx('space-y-3', !admin && 'pointer-events-none opacity-50')}
        onSubmit={(e) => {
          e.preventDefault()
          void run(() => api.admin.createService(svc), `Service “${svc.slug}” created.`)
        }}
      >
        <h2 className="font-semibold text-ink">
          New service {!admin && <span className="text-xs font-normal text-subtle">(admins only)</span>}
        </h2>
        <Field label="Slug" value={svc.slug} onChange={(v) => setSvc({ ...svc, slug: v })} required />
        <Field label="Title" value={svc.title} onChange={(v) => setSvc({ ...svc, title: v })} required />
        <Field
          label="Description"
          value={svc.description}
          onChange={(v) => setSvc({ ...svc, description: v })}
        />
        <Button type="submit" variant="primary" size="sm">
          Create service
        </Button>
      </form>

      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          void run(() => api.admin.createTopic(topic), `Topic “${topic.path}” created.`)
        }}
      >
        <h2 className="font-semibold text-ink">New topic</h2>
        <Field
          label="Service slug"
          value={topic.service}
          onChange={(v) => setTopic({ ...topic, service: v })}
          required
        />
        <Field
          label="Path"
          hint="Slash path from the service root, e.g. doctor-desk/exams. Missing levels are created."
          value={topic.path}
          onChange={(v) => setTopic({ ...topic, path: v })}
          required
        />
        <Field label="Title" value={topic.title} onChange={(v) => setTopic({ ...topic, title: v })} required />
        <Button type="submit" variant="primary" size="sm">
          Create topic
        </Button>
      </form>
    </div>
  )
}

function Field({
  label,
  hint,
  value,
  onChange,
  required,
}: {
  label: string
  hint?: string
  value: string
  onChange: (v: string) => void
  required?: boolean
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted">{label}</span>
      <input
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 w-full rounded-[var(--radius-sh)] border border-line bg-surface px-3 text-sm text-ink focus:border-accent focus:outline-none"
      />
      {hint && <span className="mt-1 block text-xs text-subtle">{hint}</span>}
    </label>
  )
}

const Wrap = ({ children }: { children: React.ReactNode }) => (
  <div className="mx-auto max-w-4xl px-1 py-10 lg:px-8">{children}</div>
)
